// AdminPanel/pages/LeadsPage.jsx
import { useState, useEffect, useCallback } from "react";
import { T, Icon } from "../AdminPanel";
import { useApi } from "../AdminPanel";

const STATUS_CONFIG = {
  new: { label: "Новая", color: "#10c9a5", bg: "rgba(16,201,165,0.1)" },
  in_progress: { label: "В работе", color: "#c9a500", bg: "rgba(201,165,0,0.1)" },
  done: { label: "Выполнена", color: "#123133", bg: "rgba(18,49,51,0.08)" },
  rejected: { label: "Отклонена", color: "#c0392b", bg: "rgba(192,57,43,0.1)" },
};

const STATUS_OPTIONS = [
  { value: "new", label: "Новая" },
  { value: "in_progress", label: "В работе" },
  { value: "done", label: "Выполнена" },
  { value: "rejected", label: "Отклонена" },
];

export default function LeadsPage({ token, toast, onExpired }) {
  const { get, del, patch } = useApi(token, onExpired);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [deleting, setDeleting] = useState(null);
  const [changingStatus, setChangingStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get("/leads/");
      setLeads(data);
    } catch (e) {
      if (e.message !== "SESSION_EXPIRED") toast("Ошибка загрузки", "error");
    } finally {
      setLoading(false);
    }
  }, [get, toast]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, newStatus) => {
    setChangingStatus(id);
    try {
      await patch(`/leads/${id}/status`, { status: newStatus });
      toast("Статус обновлён", "success");
      setLeads(prev => prev.map(l => l.id === id ? { ...l, status: newStatus } : l));
    } catch {
      toast("Ошибка", "error");
    } finally {
      setChangingStatus(null);
    }
  };

  const deleteLead = async (id) => {
    try {
      await del(`/leads/${id}`);
      toast("Заявка удалена", "success");
      setLeads(prev => prev.filter(l => l.id !== id));
      setDeleting(null);
    } catch {
      toast("Ошибка удаления", "error");
    }
  };

  const getStatusBadge = (status) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
    return (
      <span style={{
        display: "inline-block",
        padding: "4px 12px",
        fontSize: "11px",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        background: cfg.bg,
        color: cfg.color,
        borderRadius: "2px",
      }}>
        {cfg.label}
      </span>
    );
  };

  const filteredLeads = filter === "all" 
    ? leads 
    : leads.filter(l => l.status === filter);

  const counts = {
    all: leads.length,
    new: leads.filter(l => l.status === "new").length,
    in_progress: leads.filter(l => l.status === "in_progress").length,
    done: leads.filter(l => l.status === "done").length,
    rejected: leads.filter(l => l.status === "rejected").length,
  };

  return (
    <div>
      <div className="adm-eyebrow">CRM / Заявки</div>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 28 }}>
        <h1 style={{ fontFamily: "'Oswald', sans-serif", fontSize: 32, fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Заявки с сайта
        </h1>
        <button className="adm-btn" onClick={load}>{Icon.Refresh} Обновить</button>
      </div>

      {/* Фильтры */}
      <div className="adm-filters" style={{ marginBottom: 24 }}>
        {[
          { value: "all", label: "Все" },
          { value: "new", label: "Новые" },
          { value: "in_progress", label: "В работе" },
          { value: "done", label: "Выполненные" },
          { value: "rejected", label: "Отклонённые" },
        ].map(f => (
          <button
            key={f.value}
            className={`adm-filter-btn${filter === f.value ? " active" : ""}`}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
            <span className="adm-filter-count">{counts[f.value] || 0}</span>
          </button>
        ))}
      </div>

      {/* Таблица заявок */}
      <div className="adm-card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div className="adm-empty"><span className="adm-spinner" /></div>
        ) : filteredLeads.length === 0 ? (
          <div className="adm-empty">Нет заявок в этом статусе</div>
        ) : (
          <table className="adm-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Клиент</th>
                <th>Телефон</th>
                <th>Тип объекта</th>
                <th>Статус</th>
                <th>Дата</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map(lead => (
                <tr key={lead.id}>
                  <td style={{ verticalAlign: "top", paddingTop: 16 }}>
                    {lead.comment && (
                      <span title={lead.comment} style={{ cursor: "help", color: T.textHint }}>📝</span>
                    )}
                  </td>
                  <td style={{ fontWeight: 500 }}>{lead.name}</td>
                  <td>
                    <a href={`tel:${lead.phone}`} style={{ color: T.text, textDecoration: "none" }}>
                      {lead.phone}
                    </a>
                  </td>
                  <td style={{ color: T.textMuted, fontSize: 12 }}>
                    {lead.object_type || "—"}
                  </td>
                  <td>
                    <select
                      value={lead.status}
                      onChange={(e) => updateStatus(lead.id, e.target.value)}
                      disabled={changingStatus === lead.id}
                      style={{
                        padding: "6px 24px 6px 10px",
                        fontSize: 11,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        fontFamily: "inherit",
                        background: STATUS_CONFIG[lead.status]?.bg || "transparent",
                        color: STATUS_CONFIG[lead.status]?.color || T.text,
                        border: `1px solid ${STATUS_CONFIG[lead.status]?.color + "40" || T.border}`,
                        borderRadius: 0,
                        cursor: "pointer",
                        appearance: "none",
                        WebkitAppearance: "none",
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='${STATUS_CONFIG[lead.status]?.color.replace("#", "%23") || "#123133"}' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 8px center",
                      }}
                    >
                      {STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value} style={{ textTransform: "none" }}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ color: T.textHint, fontSize: 12, whiteSpace: "nowrap" }}>
                    {new Date(lead.created_at).toLocaleDateString("ru-RU", {
                      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"
                    })}
                  </td>
                  <td>
                    <button 
                      className="adm-btn adm-btn--sm adm-btn--danger" 
                      onClick={() => setDeleting(lead)}
                      style={{ padding: "4px 10px" }}
                    >
                      {Icon.Trash}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Модалка удаления */}
      {deleting && (
        <div className="adm-modal-wrap" onClick={e => e.target === e.currentTarget && setDeleting(null)}>
          <div className="adm-modal" style={{ maxWidth: 400 }}>
            <div className="adm-modal__header">
              <span className="adm-modal__title">Удаление заявки</span>
              <button className="adm-btn adm-btn--sm" onClick={() => setDeleting(null)}>{Icon.Close}</button>
            </div>
            <div className="adm-modal__body">
              <p style={{ fontSize: 14, color: T.textMuted }}>
                Удалить заявку от <strong>{deleting.name}</strong> ({deleting.phone})?
              </p>
              {deleting.comment && (
                <div style={{ marginTop: 12, padding: 10, background: "rgba(18,49,51,0.04)", fontSize: 12 }}>
                  <strong>Комментарий:</strong> {deleting.comment}
                </div>
              )}
            </div>
            <div className="adm-modal__footer">
              <button className="adm-btn" onClick={() => setDeleting(null)}>Отмена</button>
              <button className="adm-btn adm-btn--danger" onClick={() => deleteLead(deleting.id)}>
                {Icon.Trash} Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}